"use client";

import { useEffect, useRef, useState } from "react";
import { SearchIcon } from "../icons";

const ROTATE_MS = 3400;
const SWAP_MS = 280;

/**
 * The hero search: one field, conversational examples rotating behind it. Rotation pauses while the search has focus,
 * stops for reduced motion, and the example hides whenever the field has text. The hiding is CSS (`:placeholder-shown`), so it follows
 * the field's real value (typed, pasted, autofilled, or restored by the browser on back/forward) without waiting for a script. Submits to the listings search (`q`).
 */
export function HeroSearch({ action, examples, fallback, noun }: { action: string; examples: string[]; fallback: string; noun: "home" | "room" }) {
  const list = examples.length > 0 ? examples : [fallback];
  const [index, setIndex] = useState(0);
  const [leaving, setLeaving] = useState(false);
  const form = useRef<HTMLFormElement>(null);

  useEffect(() => {
    if (list.length < 2 || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    let swap: ReturnType<typeof setTimeout> | undefined;
    const timer = setInterval(() => {
      if (form.current?.contains(document.activeElement)) return;
      setLeaving(true);
      swap = setTimeout(() => {
        setIndex((current) => (current + 1) % list.length);
        setLeaving(false);
      }, SWAP_MS);
    }, ROTATE_MS);
    return () => {
      clearInterval(timer);
      if (swap) clearTimeout(swap);
    };
  }, [list.length]);

  return (
    <form ref={form} className="hf-search" role="search" action={action} method="get">
      <label className="hf-search-field">
        <span className="hf-search-ic">
          <SearchIcon />
        </span>
        <input
          name="q"
          type="search"
          enterKeyHint="search"
          autoComplete="off"
          aria-label={`Search ${noun}s by house, price or move-in date`}
          placeholder=" "
        />
        <span className="hf-search-ph" aria-hidden="true" data-leaving={leaving ? "" : undefined}>
          {list[index % list.length]}
        </span>
      </label>
      <button className="hf-btn hf-btn-accent hf-search-go" type="submit" aria-label="Search">
        <SearchIcon />
        <span aria-hidden="true">Search</span>
      </button>
    </form>
  );
}
